import { useEffect, useRef } from "react";

// Marks a grid Card as the only thing printed; the matching rules live in Common/Table/Table.css (ADO #15108).
const PRINT_LANDSCAPE_CLASS = 'datagrid-print-landscape';
const STYLE_ID = 'datagrid-print-landscape-page';
// Letter landscape minus the 0.5cm @page margins; A4 is wider, so this fits both.
export const PRINTABLE_WIDTH_PX = 1018;
// A column counts as "in view" once at least this share of it shows on screen.
const IN_VIEW_MIN_SHARE = 0.25;

// Card width that covers every column currently in view, including a partially visible last one.
const inViewWidth = (card, scroller) => {
    const view = scroller.getBoundingClientRect();
    const rights = Array.from(card.querySelectorAll('.MuiDataGrid-columnHeader'))
        .map((header) => header.getBoundingClientRect())
        .filter((r) => Math.min(r.right, view.right) - Math.max(r.left, view.left) >= r.width * IN_VIEW_MIN_SHARE)
        .map((r) => r.right);
    return Math.ceil(Math.max(view.right, ...rights) - card.getBoundingClientRect().left);
};

// Prints the host grid alone, in landscape, zoomed so every in-view column fits the sheet; returns the Card ref + className.
const usePrintLandscape = (enabled = false) => {
    const cardRef = useRef(null);

    useEffect(() => {
        if (!enabled) return undefined;

        // The --print-* values are read only by the @media print rules, so the screen never changes while the dialog is open.
        const fitToSheet = () => {
            const card = cardRef.current;
            const scroller = card?.querySelector('.MuiDataGrid-virtualScroller');
            if (!scroller || document.getElementById(STYLE_ID)) return;
            // Pinning the on-screen width keeps the column virtualizer from dropping columns at paper width; zoom fits them on the sheet.
            const width = inViewWidth(card, scroller);
            card.style.setProperty('--print-width', `${width}px`);
            card.style.setProperty('--print-zoom', String(Math.min(1, PRINTABLE_WIDTH_PX / width)));
            card.style.setProperty('--print-scroll-left', `${scroller.scrollLeft}px`);
            // A named @page cannot flip Chrome's sheet orientation, so inject a default landscape @page only while printing.
            const style = document.createElement('style');
            style.id = STYLE_ID;
            style.textContent = '@page { size: landscape; margin: 0.5cm; }';
            document.head.appendChild(style);
        };
        const reset = () => {
            const card = cardRef.current;
            if (card) {
                ['--print-width', '--print-zoom', '--print-scroll-left'].forEach((p) => card.style.removeProperty(p));
            }
            document.getElementById(STYLE_ID)?.remove();
        };

        // beforeprint/afterprint fire for Ctrl+P and right-click Print; other pages' print rules stay untouched.
        window.addEventListener('beforeprint', fitToSheet);
        window.addEventListener('afterprint', reset);
        return () => {
            window.removeEventListener('beforeprint', fitToSheet);
            window.removeEventListener('afterprint', reset);
            reset();
        };
    }, [enabled]);

    return { ref: cardRef, className: enabled ? PRINT_LANDSCAPE_CLASS : undefined };
};

export default usePrintLandscape;
